import { useMemo, useState } from 'react';

import { ControlledDevice } from '@crossnokaye/typescript-sdk/models/components';
import { cn, Popover, PopoverContent, PopoverTrigger } from '@crossnokaye/ux-common';

import { SequencerDevice } from '@repo/sdk/models/components';

import { NavLink } from '@/components/navigation/nav-link';

// Helper to get common properties from either device type
const getDeviceProps = (device: ControlledDevice | SequencerDevice) => {
  if ('deviceId' in device) {
    // ControlledDevice
    return {
      id: device.deviceId,
      displayName: device.name ?? device.alias,
    };
  }
  // SequencerDevice
  return {
    id: device.id,
    displayName: device.displayName,
  };
};

interface SequencerNavProps {
  children?: React.ReactNode;
  onSelect?: () => void;
  routeId: string;
  sequencerDevices: ControlledDevice[] | SequencerDevice[];
}

export const SequencerNav = ({
  children,
  onSelect,
  routeId,
  sequencerDevices,
}: SequencerNavProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Sort sequencers by display name
  const sequencers = useMemo(() => {
    if (!sequencerDevices) return [];
    return [...sequencerDevices].sort((a, b) => {
      const propsA = getDeviceProps(a);
      const propsB = getDeviceProps(b);
      return (propsA.displayName ?? '').localeCompare(propsB.displayName ?? '');
    });
  }, [sequencerDevices]);

  const handleSequencerClick = () => {
    onSelect?.();
    setIsOpen(false);
  };

  // Shared sequencer list rendering
  const sequencerList = (
    <>
      {sequencers.length === 0 ? (
        <div className="rounded-sm px-2 py-1.5 text-sm text-muted-foreground">
          No sequencers found
        </div>
      ) : (
        sequencers.map((sequencer) => {
          const { id, displayName } = getDeviceProps(sequencer);
          return (
            <NavLink
              key={id}
              routeId={routeId}
              searchParams={{ equipmentId: id }}
              onClick={handleSequencerClick}
            >
              <div
                className={cn(
                  'rounded-sm px-2 py-1.5 text-sm font-medium outline-none transition-colors cursor-pointer',
                  'focus:bg-accent hover:bg-accent'
                )}
                data-testid={`sequencer-nav-${routeId}-item-${id}`}
              >
                {displayName}
              </div>
            </NavLink>
          );
        })
      )}
    </>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className={'w-auto border-0 p-1 text-left text-popover-foreground z-overlay min-w-32'}
        side={'right'}
        align={'start'}
        alignOffset={0}
        sideOffset={4}
        withPortal
        data-testid={`sequencer-nav-dropdown-${routeId}`}
      >
        {sequencerList}
      </PopoverContent>
    </Popover>
  );
};
